import Spacer from "@components/atoms/Spacer";
import Fonts from "@constants/Fonts";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { StyleSheet, ScrollView } from "react-native";
import { object, string } from "@lib/util/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { View, Text, Card, Wrapper } from "@components/atoms/Themed";
import Colors from "@constants/Colors";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import AttachmentsUploader, {
  isUploadedFile,
} from "@components/molecules/AttachmentsUploader";
import { ImageFile } from "@components/molecules/ImagePicker";
import { MediaTypeOptions } from "expo-image-picker";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import { makeUploadablesFromFileList } from "@lib/util/relaySupport";
import {
  TextField,
  TextArea,
  CopyField,
  ErrorText,
} from "@components/molecules/TextInput";
import GradientButton from "@components/atoms/GradientButton";
import { PostDraftTiktok$key } from "@generated/PostDraftTiktok.graphql";
import { PostDraftTiktokMutation } from "@generated/PostDraftTiktokMutation.graphql";
import {
  KeyboardId,
  TiktokContentMaxLength,
  TiktokTitleMaxLength,
  PostMaxImageSize,
  PostMaxVideoSize,
} from "@constants/App";
import {
  graphql,
  useFragment,
  useMutation,
  ConnectionHandler,
} from "react-relay/hooks";
import Divider from "@components/atoms/Divider";
import ConfirmDialog from "@components/organisms/PostDraft/PostDraftConfirmDialog";
import { PostDraftTiktokRemoveMutation } from "@generated/PostDraftTiktokRemoveMutation.graphql";
import { resolveError } from "@lib/util/error";

const chatDraft = graphql`
  fragment PostDraftTiktok on Post {
    id
    title
    content
    campaign {
      purpose
      avoidedSentence
    }
    attachments {
      id
      file
      duration
      contentType
    }
  }
`;

const postMutation = graphql`
  mutation PostDraftTiktokMutation(
    $input: UpdatePostMutationInput!
    $connections: [ID!]!
  ) {
    updatePost(input: $input) {
      __typename
      ... on UpdatePostSuccess {
        post {
          id
          status
          title
          content
          attachments {
            file
            duration
            contentType
          }
        }
        commentEdges @prependEdge(connections: $connections) {
          cursor
          node {
            id
            content
            action
            stamp
            extraInfo
            extraType
            createdAt
            commentable {
              userType
              avatar {
                file
              }
            }
            ogps {
              id
              title
              image
              url
              description
            }
            attachments {
              id
              file
              duration
              contentType
            }
            post {
              id
            }
          }
        }
      }
      ... on UpdatePostError {
        message
      }
    }
  }
`;

type TiktokInput = {
  title: string;
  content: string;
};

const removeImagesMutation = graphql`
  mutation PostDraftTiktokRemoveMutation(
    $input: RemoveAttachmentsMutationInput!
  ) {
    removeAttachments(input: $input) {
      __typename
      ... on Attachments {
        attachments {
          id
        }
      }
      ... on RemoveAttachmentsError {
        message
      }
    }
  }
`;

export default function PostDraftTiktok({
  postFragment,
  status,
  onClose,
  onChange,
  onLoading,
}: {
  postFragment: PostDraftTiktok$key;
  status: "draft" | "modify" | "review" | "approved";
  onClose: () => void;
  onChange: (isChange: boolean) => void;
  onLoading: (isLoading: boolean) => void;
}) {
  const data = useFragment(chatDraft, postFragment);
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  const [postable, setPostable] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [commit] = useMutation<PostDraftTiktokMutation>(postMutation);
  const uploaded = useMemo(() => data.attachments.map((row) => row), [data]);
  const canEdit = useMemo<boolean>(
    () => ["draft", "modify"].indexOf(status) !== -1,
    [status]
  );

  const [removeImageList, setRemoveImageList] = useState<string[] | null>(null);
  const [commitRemoveImages] =
    useMutation<PostDraftTiktokRemoveMutation>(removeImagesMutation);

  const connectionID = ConnectionHandler.getConnectionID(
    data.id,
    "Chat_comments"
  );
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<TiktokInput>({
    defaultValues: {
      title: data.title ?? "",
      content: data.content ?? "",
    },
    mode: "all",
    resolver: yupResolver(
      object().shape({
        title: string()
          .trim()
          .max(
            TiktokTitleMaxLength,
            `タイトルは${TiktokTitleMaxLength}文字以内で入力してください`
          )
          .required("入力してください"),
        content: string()
          .trim()
          .max(
            TiktokContentMaxLength,
            `投稿内容は${TiktokContentMaxLength}文字以内で入力してください`
          )
          .required("入力してください"),
      })
    ),
  });

  useEffect(() => {
    setPostable(
      isValid &&
        canEdit &&
        (data.attachments.length > 0 || selectedImages.length > 0)
    );
  }, [isValid, selectedImages, data, canEdit]);

  const save = useCallback(async () => {
    await handleSubmit(async ({ content, title }: TiktokInput) => {
      try {
        setConfirm(false);
        onLoading(true);
        const files: File[] = [];
        selectedImages.forEach((image) => {
          if (!isUploadedFile(image)) {
            files.push(image.file);
          }
        });
        const uploadables = await makeUploadablesFromFileList(
          "variables.input.files",
          files
        );
        await new Promise<void>((resolve, reject) => {
          if (removeImageList !== null && removeImageList.length > 0) {
            commitRemoveImages({
              variables: {
                input: {
                  ids: removeImageList,
                },
              },
              onCompleted({ removeAttachments }) {
                if (removeAttachments.__typename === "Attachments") {
                  resolve();
                } else {
                  reject(
                    removeAttachments.__typename === "RemoveAttachmentsError"
                      ? removeAttachments.message
                      : "削除できませんでした"
                  );
                }
              },
            });
          } else {
            resolve();
          }
        });
        await new Promise<void>((resolve, reject) => {
          commit({
            variables: {
              input: {
                id: data.id,
                content,
                title,
                files: selectedImages.map(() => null),
              },
              connections: [connectionID],
            },
            uploadables,
            onCompleted({ updatePost }) {
              if (updatePost.__typename === "UpdatePostSuccess") {
                resolve();
              } else {
                reject(
                  updatePost.__typename === "UpdatePostError"
                    ? updatePost.message
                    : "保存できませんでした"
                );
              }
            },
          });
        });
        onClose();
      } catch (e: unknown) {
        setError(resolveError(e).message);
        onLoading(false);
      }
    })();
  }, [
    commit,
    commitRemoveImages,
    connectionID,
    data,
    onClose,
    onLoading,
    removeImageList,
    selectedImages,
    handleSubmit,
  ]);

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView keyboardVerticalOffset={48}>
        <ScrollView>
          <Wrapper>
            <Spacer height={8} />
            <Card style={styles.card}>
              <Text style={styles.text}>
                TikTokに投稿する動画・画像をアップロードしてください。（最大10枚、写真
                {PostMaxImageSize}MB、動画{PostMaxVideoSize}MBまで）
              </Text>
              <Spacer height={16} />

              <AttachmentsUploader
                items={uploaded}
                maxImageSize={PostMaxImageSize}
                maxVideoSize={PostMaxVideoSize}
                onChange={(e: ImageFile[]) => {
                  setSelectedImages(e);
                  onChange(true);
                }}
                onMessage={setError}
                onPickEnd={() => onLoading(false)}
                onPickStart={() => onLoading(true)}
                pickupMediaType={MediaTypeOptions.All}
                removeImageIds={(imageIds) => {
                  setRemoveImageList([...imageIds]);
                  onChange(true);
                }}
                uploadable={canEdit}
              />
            </Card>

            <Spacer height={16} />

            <Card style={styles.card}>
              <Text style={styles.text}>
                [PR]に続けてタイトルを入力してください。
              </Text>
              <Spacer height={16} />
              <View style={styles.prInputForm}>
                <Text style={styles.prSubText}>[PR]</Text>
                <Spacer width={16} />
                <View style={styles.prInputItem}>
                  {canEdit ? (
                    <>
                      <TextField
                        control={control}
                        inputAccessoryViewID={KeyboardId}
                        maxLength={TiktokTitleMaxLength}
                        name="title"
                        onChange={() => onChange(true)}
                        placeholder="タイトル"
                        placeholderTextColor={Colors.gray}
                        style={{ height: 48 }}
                      />
                      {!isValid && errors.title !== undefined && (
                        <ErrorText error={errors.title.message} />
                      )}
                    </>
                  ) : (
                    <CopyField value={data.title} />
                  )}
                </View>
              </View>
            </Card>

            <Spacer height={16} />

            <Card style={styles.card}>
              <Text style={styles.title}>撮影の仕方</Text>
              <Spacer height={8} />
              <Text style={styles.text}>{data.campaign.purpose}</Text>
              <Spacer height={24} />
              <Text style={styles.title}>投稿に関するNG事項</Text>
              <Spacer height={8} />
              <Text style={styles.text}>{data.campaign.avoidedSentence}</Text>
            </Card>

            <Spacer height={16} />

            <Card style={styles.card}>
              <Text style={styles.text}>投稿時の内容を記載してください。</Text>

              <Spacer height={16} />

              {canEdit ? (
                <>
                  <TextArea
                    control={control}
                    inputAccessoryViewID={KeyboardId}
                    label="投稿内容"
                    maxLength={TiktokContentMaxLength}
                    name="content"
                    numberOfLines={9}
                    onChange={() => onChange(true)}
                    useCounter
                  />
                  {!isValid && errors.content !== undefined && (
                    <ErrorText error={errors.content.message} />
                  )}
                </>
              ) : (
                <CopyField multiline value={data.content} />
              )}
            </Card>
          </Wrapper>

          <Spacer height={40} />
        </ScrollView>
      </KeyboardAvoidingView>

      {status !== "approved" && status !== "review" && (
        <>
          <Divider />
          <Card style={styles.action}>
            <GradientButton
              disabled={!postable}
              onPress={() => setConfirm(true)}
              textStyle={styles.actionText}
              title={
                status === "draft"
                  ? "下書きを作成する"
                  : status === "modify"
                  ? "下書きを修正する"
                  : "レビュー中"
              }
            />
            {!postable && canEdit && (
              <>
                <Spacer height={8} />
                <Text style={styles.error}>入力内容に不備があります</Text>
              </>
            )}
          </Card>
          <InputAccessoryView nativeID={KeyboardId} />
        </>
      )}
      {confirm && (
        <ConfirmDialog cancel={() => setConfirm(false)} save={save} />
      )}
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  text: {
    ...Fonts.lr130,
    color: Colors.black,
  },
  title: {
    ...Fonts.lb100,
    color: Colors.gray,
  },
  alert: {
    ...Fonts.lb130,
    color: Colors.orange,
  },
  guide: {
    ...Fonts.lr130,
    color: Colors.gray,
  },
  guides: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  prInputForm: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  prSubText: {
    ...Fonts.xlb100,
  },
  prInputItem: {
    flex: 1,
  },
  action: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  actionText: {
    color: Colors.white,
    ...Fonts.xlb100,
  },
  error: {
    color: Colors.orange,
  },
});
